import React, { useState } from "react";
import "./AddProduct.css";
import { useNavigate } from "react-router-dom";
import upload_area from "../Assets/upload_area.svg";
import { backend_url } from "../../App";
import { toast, ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { menuItems } from "../../Constants/menuItems";
import Barcode from "react-barcode";
import BarcodeModal from "../BarcodeModal/BarcodeModal";
import ColorVariants from "../ColorVariants/ColorVariants";
import { isAdmin } from "../../utils/roleUtils";

const generateUniqueId = () => {
  const ts   = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `AAF-${ts}${rand}`;
};

const INIT = {
  name: "", description: "", unique_id: generateUniqueId(),
  category: "", subcategory: "", new_price: "", old_price: "",
  bestseller: "No", status: "In-Stock", quantity: "", scale: "Meters",
  material: "", weight: "", size: "", color: "", quality: "",
  disclaimer: "", washcare: "", note: "", images: [],
};

const Field = ({ label, required, children, hint }) => (
  <div className="ap-field">
    <label className="ap-label">{label}{required && <span className="ap-req"> *</span>}</label>
    {children}
    {hint && <span className="ap-field-hint">{hint}</span>}
  </div>
);

const AddProduct = () => {
  const [images,       setImages]       = useState([]);
  const [colors,       setColors]       = useState([]);
  const [addedProduct, setAddedProduct] = useState(null);
  const [details,      setDetails]      = useState({ ...INIT });
  const navigate = useNavigate();

  const change    = (e) => setDetails((p) => ({ ...p, [e.target.name]: e.target.value }));
  const changeCat = (e) => {
    const { name, value } = e.target;
    setDetails((p) =>
      name === "category" ? { ...p, category: value, subcategory: "" } : { ...p, [name]: value }
    );
  };

  const getSubcategories = () => {
    const cat = menuItems.find((m) => m.value === details.category);
    if (!cat) return [];
    return cat.dropdown.flatMap((sub) =>
      sub.subcategories
        ? [{ label: sub.label, value: null, isParent: true },
           ...sub.subcategories.map((n) => ({ ...n, isNested: true }))]
        : [sub]
    );
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 4) { toast.error("Max 4 images allowed!"); return; }
    const big = files.find((f) => f.size > 2 * 1024 * 1024);
    if (big) { toast.error(`"${big.name}" exceeds 2MB!`); return; }
    setImages((p) => [...p, ...files]);
  };

  const removeImage = (i) => setImages(images.filter((_, idx) => idx !== i));

  // Uploads any new (File) images for each color variant and returns the
  // final { name, hex, images: [url,...] } array ready to send to the server.
  const resolveColors = async () => {
    const resolved = [];
    for (const c of colors) {
      if (!c.name.trim() || !c.hex) continue; // skip empty/incomplete rows
      const newFiles = c.images.filter((img) => typeof img !== "string");
      let urls = c.images.filter((img) => typeof img === "string");
      if (newFiles.length > 0) {
        const fd = new FormData();
        newFiles.forEach((f) => fd.append("images", f));
        const res = await fetch(`${backend_url}/uploadcolorimages`, { method: "POST", body: fd });
        const data = await res.json();
        if (data.success) urls = [...urls, ...data.urls];
      }
      resolved.push({ name: c.name.trim(), hex: c.hex, images: urls });
    }
    return resolved;
  };

  const validateForm = () => {
    const { name, description, unique_id, category, subcategory, new_price,
      old_price, quantity, scale, material, weight, size, color, quality, status, bestseller } = details;
    return name && description && unique_id && category && subcategory &&
      new_price && old_price && quantity && scale && material && weight &&
      size && color && quality && status && bestseller && images.length > 0;
  };

  const handleSubmit = async () => {
    if (images.length === 0) { toast.error("Please upload at least one image", { autoClose: 2000, transition: Slide }); return; }
    if (!validateForm())     { toast.error("Please fill all required fields",  { autoClose: 2000, transition: Slide }); return; }
    try {
      const finalColors = await resolveColors();
      const formData = new FormData();
      Object.entries(details).forEach(([k, v]) => formData.append(k, v));
      images.forEach((img) => formData.append("images", img));
      formData.append("colors", JSON.stringify(finalColors));
      const res  = await fetch(`${backend_url}/addproduct`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setAddedProduct({ name: details.name, unique_id: details.unique_id,
          old_price: details.old_price, new_price: details.new_price,
          images: images.map((img) => URL.createObjectURL(img)) });
        toast.success(data.pending ? "New product sent to Admin for approval" : "Product added successfully", { autoClose: 2000, transition: Slide });
        setSavedBanner(data.pending ? "pending" : "live");
        setDetails({ ...INIT, unique_id: generateUniqueId() });
        setImages([]);
        setColors([]);
      } else {
        toast.error(data.message || "Failed to add product");
      }
    } catch {
      toast.error("An error occurred while adding the product");
    }
  };

  const [savedBanner, setSavedBanner] = useState(false);

  const handleCancel = () => { setDetails({ ...INIT }); setImages([]); setColors([]); navigate(-1); };

  const handleAddAnother = () => {
    setSavedBanner(false);
    setDetails({ ...INIT, unique_id: generateUniqueId() });
    setImages([]);
    setColors([]);
  };

  const offerPrice = details.old_price && details.new_price
    ? (details.old_price - (details.old_price * details.new_price) / 100).toFixed(2)
    : null;

  return (
    <>
      <div className="ap-page">

        {/* ── Header ── */}
        <div className="ap-page-header">
          <div className="ap-header-left">
            <button className="ap-back-btn" onClick={() => navigate("/listproduct")}>
              ← Products
            </button>
            <div>
              <h1 className="ap-page-title">Add New Product</h1>
              <p className="ap-page-sub">
                {isAdmin()
                  ? "Fill in the details below to list a new product"
                  : "Fill in the details below — the product goes live after Admin approval"}
              </p>
            </div>
          </div>
        </div>

        {/* ── Success Banner ── */}
        {savedBanner && (
          <div className="ap-success-banner">
            <div className="ap-success-left">
              <span className="ap-success-icon">{savedBanner === "pending" ? "⏳" : "✓"}</span>
              <div>
                <p className="ap-success-title">
                  {savedBanner === "pending" ? "Sent for Admin approval!" : "Product added successfully!"}
                </p>
                <p className="ap-success-sub">
                  {savedBanner === "pending"
                    ? "The product will go live once an Admin approves it."
                    : "The new product is now live in your catalogue."}
                </p>
              </div>
            </div>
            <div className="ap-success-actions">
              <button className="ap-btn-back" onClick={() => navigate("/listproduct")}>
                ← Back to Products
              </button>
              <button className="ap-btn-another" onClick={handleAddAnother}>
                + Add Another
              </button>
            </div>
          </div>
        )}

        {/* ── 3-col grid ── */}
        <div className="ap-grid">

          {/* ── COL 1: Basic + Pricing ── */}
          <div className="ap-col ap-col-1">

            {/* Basic Info */}
            <div className="ap-card">
              <div className="ap-card-hd"><span>📋</span> Basic Information</div>

              <Field label="Product Name" required>
                <input className="ap-input" type="text" name="name"
                  value={details.name} onChange={change} maxLength={200}
                  placeholder="e.g. Champagne Blush Silk Saree" />
              </Field>

              <Field label="Unique ID" required>
                <div className="ap-uid-row">
                  <input className="ap-input" type="text" name="unique_id"
                    value={details.unique_id} onChange={change} />
                  <button className="ap-regen-btn"
                    onClick={() => setDetails((p) => ({ ...p, unique_id: generateUniqueId() }))}>
                    ↻ New ID
                  </button>
                </div>
              </Field>

              {details.unique_id && (
                <div className="ap-barcode-wrap">
                  <Barcode value={details.unique_id} width={1.3} height={44}
                    fontSize={10} displayValue background="#fafafa" />
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="ap-card">
              <div className="ap-card-hd"><span>💰</span> Pricing</div>

              <div className="ap-row2">
                <Field label="MRP / Price (₹)" required>
                  <input className="ap-input" type="number" name="old_price"
                    value={details.old_price} onChange={change} placeholder="0" />
                </Field>
                <Field label="Discount (%)">
                  <input className="ap-input" type="number" name="new_price"
                    value={details.new_price} onChange={change} placeholder="0" />
                </Field>
              </div>

              {offerPrice && (
                <div className="ap-offer-strip">
                  <span className="ap-offer-label">Offer Price</span>
                  <span className="ap-offer-val">₹{offerPrice}</span>
                  <span className="ap-off-badge">{details.new_price}% OFF</span>
                </div>
              )}
            </div>

          </div>

          {/* ── COL 2: Classification + Specs ── */}
          <div className="ap-col ap-col-2">

            {/* Classification */}
            <div className="ap-card">
              <div className="ap-card-hd"><span>🏷️</span> Classification</div>

              <div className="ap-row2">
                <Field label="Category" required>
                  <select className="ap-select" name="category"
                    value={details.category} onChange={changeCat}>
                    <option value="">Select Category</option>
                    {menuItems.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Subcategory" required>
                  <select className="ap-select" name="subcategory"
                    value={details.subcategory} onChange={changeCat}
                    disabled={!details.category}>
                    <option value="">Select Subcategory</option>
                    {getSubcategories().map((s) => (
                      <option key={s.value || s.label} value={s.value || ""}
                        disabled={s.isParent}
                        className={s.isParent ? "option-parent" : s.isNested ? "option-nested" : "option-child"}>
                        {s.isNested ? `⎯ ${s.label}` : s.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="ap-row2">
                <Field label="Availability" required>
                  <select className="ap-select" name="status" value={details.status} onChange={change}>
                    <option value="In-Stock">In-Stock</option>
                    <option value="Low Stock">Low Stock</option>
                    <option value="Out of Stock">Out of Stock</option>
                  </select>
                </Field>
                <Field label="Best Seller">
                  <select className="ap-select" name="bestseller" value={details.bestseller} onChange={change}>
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </Field>
              </div>
            </div>

            {/* Specifications */}
            <div className="ap-card">
              <div className="ap-card-hd"><span>📐</span> Specifications</div>

              <div className="ap-row2">
                <Field label="Quantity" required>
                  <input className="ap-input" type="text" name="quantity"
                    value={details.quantity} onChange={change} placeholder="e.g. 50" />
                </Field>
                <Field label="Scale">
                  <select className="ap-select" name="scale" value={details.scale} onChange={change}>
                    <option value="Meters">Meters</option>
                    <option value="Pcs">Pieces</option>
                  </select>
                </Field>
              </div>

              <div className="ap-row3">
                <Field label="Material" required>
                  <input className="ap-input" type="text" name="material"
                    value={details.material} onChange={change} maxLength={20} placeholder="e.g. Silk" />
                </Field>
                <Field label="Weight">
                  <input className="ap-input" type="text" name="weight"
                    value={details.weight} onChange={change} maxLength={20} placeholder="e.g. 400g" />
                </Field>
                <Field label="Size">
                  <input className="ap-input" type="text" name="size"
                    value={details.size} onChange={change} maxLength={20} placeholder="e.g. PLC 22" />
                </Field>
              </div>

              <div className="ap-row2">
                <Field label="Color">
                  <input className="ap-input" type="text" name="color"
                    value={details.color} onChange={change} maxLength={20} placeholder="e.g. Navy Blue" />
                </Field>
                <Field label="Quality">
                  <input className="ap-input" type="text" name="quality"
                    value={details.quality} onChange={change} placeholder="e.g. Best Quality" />
                </Field>
              </div>
            </div>

          </div>

          {/* ── COL 3: Images (sticky) ── */}
          <div className="ap-col ap-col-3">
            <div className="ap-card ap-card-sticky">
              <div className="ap-card-hd"><span>🖼️</span> Product Images</div>
              <p className="ap-img-hint">
                Up to 4 images · max 2MB each<br />
                <strong>First image</strong> = thumbnail
              </p>

              <label htmlFor="ap-file" className="ap-upload-zone">
                <img src={upload_area} alt="Upload" className="ap-upload-icon" />
                <span className="ap-upload-text">Click to upload</span>
                <span className="ap-upload-sub">PNG, JPG, WEBP</span>
                <input id="ap-file" type="file" multiple accept="image/*"
                  onChange={handleFiles} hidden />
              </label>

              {images.length > 0 && (
                <div className="ap-preview-grid">
                  {images.map((img, i) => (
                    <div key={i} className="ap-preview-item">
                      <img src={URL.createObjectURL(img)} alt="preview" />
                      {i === 0 && <span className="ap-thumb-badge">Thumb</span>}
                      <button className="ap-remove-btn" onClick={() => removeImage(i)}>✕</button>
                    </div>
                  ))}
                  {images.length < 4 && (
                    <label htmlFor="ap-file" className="ap-preview-add">+</label>
                  )}
                </div>
              )}

              <div className="ap-img-counter">
                <span className={images.length === 4 ? "ap-img-full" : ""}>
                  {images.length} / 4
                </span>
              </div>
            </div>
          </div>

          {/* ── FULL-WIDTH BOTTOM: Colors + Additional Info + Description ── */}
          <div className="ap-col ap-col-bottom">
            <ColorVariants colors={colors} setColors={setColors} backendUrl={backend_url} sourceImages={images} />

            <div className="ap-card">
              <div className="ap-card-hd"><span>📝</span> Additional Details</div>

              <div className="ap-row3">
                {[
                  { name: "note",       label: "Note" },
                  { name: "disclaimer", label: "Disclaimer" },
                  { name: "washcare",   label: "Wash Care" },
                ].map(({ name, label }) => (
                  <Field key={name} label={label}>
                    <textarea className="ap-textarea ap-textarea-sm" name={name}
                      value={details[name]} onChange={change} maxLength={200}
                      placeholder={`Enter ${label.toLowerCase()}…`} />
                    <div className="ap-char-row">
                      <span className={details[name].length >= 200 ? "ap-char-error" : "ap-char-count"}>
                        {details[name].length}/200
                      </span>
                    </div>
                  </Field>
                ))}
              </div>

              <Field label="Product Description" required>
                <textarea className="ap-textarea" name="description"
                  value={details.description} onChange={change} maxLength={500}
                  placeholder="Describe the product in detail…" />
                <div className="ap-char-row">
                  <span className={details.description.length >= 500 ? "ap-char-error" : "ap-char-count"}>
                    {details.description.length}/500
                  </span>
                </div>
              </Field>
            </div>
          </div>

        </div>

        {/* ── Footer (single set of action buttons) ── */}
        {!savedBanner && (
          <div className="ap-footer-actions">
            <button className="ap-btn-cancel" onClick={handleCancel}>Cancel</button>
            <button className="ap-btn-add" onClick={handleSubmit}>
              {isAdmin() ? "+ Add Product" : "Submit for Approval"}
            </button>
          </div>
        )}

      </div>

      <ToastContainer />
      {addedProduct && (
        <BarcodeModal product={addedProduct}
          onClose={() => { setAddedProduct(null); navigate("/listproduct"); }} />
      )}
    </>
  );
};

export default AddProduct;
